import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {useQuery} from '../customHooks';
import {
	getAccountById,
	getEstimateById,
	getTaskDocumentByDocumentId,
	getSTEntityById,
	getTaskDocumentsByIdSlice
} from '../../selectors/Ares/selectors';
import Utility from '../../util/utilityfunctions';
import {getFormData} from '../../actions/FormData/formdataactions';
import {getMateriality as _getMateriality} from '../../actions/Materiality/materialityactions';
import {matchRoutes} from 'react-router';
import queryString from 'query-string';
import Env from '../../util/env';
import {
	documentSearchTypes,
	documentCategoryNames,
	serviceURL,
	positiveNumberWithOneDecimalRegex,
	numberFormats,
	IndependenceAndProfileDocumentTypes,
	documentTypesShowedOnLeftNav
} from '../uiconstants';
import {createEntityForm} from '../../actions/EntityForms/entityformsactions';

export function useAresUrlParameters() {
	const pathName = useLocation().pathname;
	const match = matchRoutes(
		[
			{path: '/guidedworkflow/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflow/:documentId/header/:headerId'},
			{path: '/guidedworkflow/:documentId/summary'},
			{path: '/guidedworkflow/:documentId'},
			{path: '/guidedworkflow'},
			{path: '/guidedworkflowv2/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/:documentId/header/:headerId'},
			{path: '/guidedworkflowv2/:documentId/summary'},
			{path: '/guidedworkflowv2/:documentId'},
			{path: '/guidedworkflowv2/changes/:documentId?'},
			{path: '/guidedworkflowv2/compare/:documentId?'},
			{path: '/guidedworkflowv2/compare/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/attributes/:documentId?'},
			{path: '/guidedworkflowv2'}
		],
		pathName
	);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		engagementId: queryStringParameters.engagementid !== undefined ? queryStringParameters.engagementid : undefined,
		documentId: queryStringParameters.documentid !== undefined ? queryStringParameters.documentid : undefined,
		headerId: undefined,
		sectionId: undefined,
		stEntityId: undefined
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const url = match[0].pathname;
		const {documentId, headerId, sectionId} = match[0].params;

		urlParameters.documentId = documentId?.toLowerCase();
		urlParameters.headerId = headerId !== undefined ? Number(headerId) : undefined;
		urlParameters.sectionId = sectionId !== undefined ? Number(sectionId) : undefined;
		urlParameters.isSummary = url.toLowerCase().includes('summary');
		urlParameters.isBodyDetailView = queryStringParameters?.isbodydetailview ? true : false;
		urlParameters.stEntityId =
			queryStringParameters.stentityid !== undefined ? Number(queryStringParameters.stentityid) : undefined;
	}

	return urlParameters;
}

export function useAresUrlTrackChangesParameters() {
	const match = matchRoutes([{path: '/guidedworkflowv2/changes/:documentId'}], useLocation().pathname);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		documentId: undefined,
		snapshotId: queryStringParameters.snapshotid !== undefined ? Number(queryStringParameters.snapshotid) : undefined,
		isTrackChanges:
			queryStringParameters.snapshotid !== undefined
				? Boolean(queryStringParameters.istrackchanges.toLowerCase() === 'true')
				: undefined
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const {documentId} = match[0].params;
		urlParameters.documentId = documentId?.toLowerCase();
	}

	return urlParameters;
}

export function useAresUrlCompareResponsesParameters() {
	const match = matchRoutes(
		[
			{path: '/guidedworkflowv2/compare/:documentId'},
			{path: '/guidedworkflowv2/compare/:documentId/header/:headerId/section/:sectionId?'}
		],
		useLocation().pathname
	);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		documentId: undefined,
		isCompareResponses:
			match && match[0]?.pathname.includes('compare') ? true : queryStringParameters?.iscompareresponses ? true : false,
		isBodyDetailView: queryStringParameters?.isbodydetailview ? true : false
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const {documentId} = match[0].params;
		urlParameters.documentId = documentId?.toLowerCase();
	}

	return urlParameters;
}
/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is in Ares (FIT) or not.
 * @returns {bool} true if the user is in Ares (FIT); false otherwise.
 */
export function useIsAresView() {
	const isAresview = useSelector((state) => state.isAresView);
	return isAresview;
}

export function useIsIndependenceOrProfile(docId = null) {
	const docType = useDocumentTypeId(docId);
	return IndependenceAndProfileDocumentTypes.includes(docType);
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true if the form is viewed in "drawer mode"; false otherwise.
 */
export function useIsDrawerMode() {
	const isDrawer = useSelector((state) => state.isDrawerMode);
	return isDrawer;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form as track changes view or not.
 * @returns {bool} true if the form is viewed in "track changes view"; false otherwise.
 */
export function useIsTrackChangesView() {
	const {isTrackChanges} = useAresUrlTrackChangesParameters();

	const isTrackChangesView = useSelector((state) => state.isTrackChangesView);

	return isTrackChanges || isTrackChangesView;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form as compare responses view or not.
 * @returns {bool} true if the form is viewed in "compare response view"; false otherwise.
 */
export function useIsCompareResponsesView() {
	const {isCompareResponses} = useAresUrlCompareResponsesParameters();

	const isCompareResponsesView = useSelector((state) => state.isCompareResponsesView);

	return isCompareResponses || isCompareResponsesView;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true if the form is viewed in "drawer mode"; false otherwise.
 */
export function useIsGuidedWorkFlowV2() {
	const isGuidedWorkFlowV2 = useSelector((state) => state.isGuidedWorkFlowV2);
	return isGuidedWorkFlowV2;
}

export function useDocumentTypeId(docId = null) {
	const document = useSelector((store) => {
		if (docId) {
			return getTaskDocumentByDocumentId(store, docId);
		}
		return getTaskDocumentByDocumentId(store, store.documentId);
	});

	return document?.documentTypeId;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true then will show only form body; false otherwise.
 */
export function useHasBodyIdFilter() {
	const hasBodyIdFilter = useSelector((state) => state.hasBodyIdFilter);
	return hasBodyIdFilter;
}

/**
 * Custom hook that returns <b>value</b> of the applied filter.
 * @returns {bool} true then will show only form body; false otherwise.
 */
export function useNavigationFilter() {
	const navigationFilter = useSelector((state) => state.navigationFilter);
	return navigationFilter;
}

/**
 * Custom hook that returns smartEvidenceSourceId related to a document.
 * @param {number} documentId documentId.
 * @returns {int} smartEvidenceSourceId if exists.
 */
export function useSmartEvidenceSourceId(documentId) {
	let smartEvidenceSourceId;

	if (documentId) {
		const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
		smartEvidenceSourceId = document?.smartEvidenceSourceId;
	}

	return smartEvidenceSourceId;
}

/**
 * Custom hook that returns smartEvidenceSourceId related to a document.
 * @param {number} documentId documentId.
 * @returns {int} smartEvidenceSourceId if exists.
 */
export function useSmartEvidenceSourceEntityId(documentId) {
	let smartEvidenceSourceEntityId;

	if (documentId) {
		const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
		smartEvidenceSourceEntityId = document?.smartEvidenceSourceEntityID;
	}

	return smartEvidenceSourceEntityId;
}

/**
 * Custom hook that returns an account related to a document.
 * @param {number} documentId documentId.
 * @returns {object} account if exists.
 */
export function useSmartEvidenceSource(documentId) {
	let smartEvidenceSource;

	if (documentId) {
		const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
		smartEvidenceSource = useSelector((state) => getAccountById(state, document?.smartEvidenceSourceId));
	}

	return smartEvidenceSource;
}

/**
 * Custom hook that returns an account related to a document.
 * @param {number} documentId documentId.
 * @returns {object} account if exists.
 */
export function useSmartEvidenceSourceForEstimate(documentId) {
	const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const smartEvidenceSource = useSelector((state) => getEstimateById(state, document.smartEvidenceSourceId));
	return smartEvidenceSource;
}

/**
 * Custom hook that returns an account related to a document.
 * @param {number} documentId documentId.
 * @returns {object} account if exists.
 */
export function useSmartEvidenceSourceForSTEntity(documentId) {
	const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const smartEvidenceSource = useSelector((state) => getSTEntityById(state, document.smartEvidenceSourceId));
	return smartEvidenceSource;
}

export function useDecimalPattern(defaultSeparator = '.') {
	const userPreferences = useSelector((state) => state.userPreferences);

	return positiveNumberWithOneDecimalRegex(
		numberFormats.find((x) => x.id === userPreferences.numberFormat)?.decimalSeparator || defaultSeparator
	);
}

/**
 * Returns the selected stEntityId, if it exists in the store, otherwise it returns the one from the url parameters.
 * @returns {string} selected stEntityId id.
 */
export function useSelectedStEntityId() {
	let selectedStEntityData = [];

	const {stEntityId} = useAresUrlParameters();
	const stEntityIdStore = useSelector((state) => state.stEntityId);

	if (!_.isEmpty(stEntityIdStore)) {
		selectedStEntityData = [stEntityIdStore.currentValue, stEntityIdStore.previousValue];
	} else if (stEntityId) {
		selectedStEntityData = [stEntityId];
	}

	return selectedStEntityData;
}

///Builds a promise using the available parameters.
///Engagement: the engagement.
///DocumentId: the document Id.
///QueryParams: query params string to apply to the built request url. Expands will be joined at the end.
///Payload: parameters to add to the get request as payload. Same level as meta object.
///Returns a promise to get form by id.
export function expandableGetFormById(
	engagement,
	documentId,
	queryParams,
	payload,
	expands = ['headers', 'headers.sections'],
	isAresView
) {
	let _queryParams = queryParams ? queryParams + '&' : '';

	if (isAresView) {
		expands.push('embeddedDocumentBodies');
		expands.push('mest');
	}

	_queryParams += 'expand=' + expands.join(',');

	return getFormData(Utility.getUrlForFormData(engagement.engagementId, documentId, _queryParams), payload);
}

export function getMateriality(engagementId, stEntityId = undefined, trackWithArea = undefined) {
	let queryStringParams = {};

	if (stEntityId !== undefined) {
		queryStringParams.stEntityId = stEntityId;
	}

	return _getMateriality(engagementId, trackWithArea, !_.isEmpty(queryStringParams) ? queryStringParams : undefined);
}

export function getDocumentsUrl(engagementId, taskId) {
	let queryStringParameters = {};

	if (taskId) {
		queryStringParameters = {
			filteredEntityType: documentSearchTypes.task,
			filteredEntityIdGuid: taskId,
			documentCategoryName: documentCategoryNames.evidence,
			includeApprovals: true,
			pageSize: -1
		};
	} else {
		queryStringParameters = {
			documentCategoryName: documentCategoryNames.evidence,
			includeApprovals: true,
			pageSize: -1
		};
	}

	const searchString = !_.isEmpty(queryStringParameters) ? `?${queryString.stringify(queryStringParameters)}` : '';
	return `${Env.getBaseURL(serviceURL, engagementId)}Documents${searchString}`;
}

export function useCreateEntityForm(engagementId, entityId, documentTypeIdToCreate, taskId, objectIds) {
	return createEntityForm(engagementId, {entityId, documentTypeId: documentTypeIdToCreate, taskId, objectIds});
}

export function useNavRelevantDocumentsFilterer() {
	const documents = useSelector((store) => getTaskDocumentsByIdSlice(store));

	return (documentIds) =>
		documentIds.filter((docId) => documentTypesShowedOnLeftNav.indexOf(documents[docId].documentTypeId) > -1);
}
